import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { INSIGHTS_AGENT_NAME } from '../../lib/agentBranding';
import { useDialogFocus } from '../../lib/useDialogFocus';
import { formatNorthStarValueInline } from '../../lib/northStarFormat';
import type {
  ConnectedSourceId,
  PerfSettings,
  ReservationSourceId,
  ReservationStatusGroup,
  ShiftFilter,
} from '../../types';
import {
  AssistantWidgetDataProvider,
  widgetNeedsFromTypes,
} from '../assistant/widgets/WidgetDataContext';
import { useNarrow } from '../context/overlay';
import { Icon } from '../shared/Icon';
import { cardMessages } from './cards';
import { ChatList } from './ChatList';
import { ContextChangePrompt, ConversationContextRow } from './ConversationContext';
import { MaitreComposer } from './MaitreComposer';
import { useMaitre } from './MaitreContext';
import { ThreadMessages } from './ThreadMessages';
import { contextStarters } from './starters';
import { useBoard } from './useBoard';
import '../../styles/maitre.css';

const ASK_STARTER_KEYS = [
  'starters.guests7',
  'starters.sourceMix',
  'starters.monthlyTrend',
  'starters.noshows',
] as const;

const NORTH_STAR_STARTER_KEYS = [
  'northStarters.profit',
  'northStarters.utilization',
  'northStarters.pay',
] as const;

/** Below this the drawer is a full-screen sheet (phones, portrait tablets). */
export const SHEET_QUERY = '(max-width: 820px)';

export type MaitreWidgetData = {
  restaurantId: string;
  regionFilter: string | null;
  perfSettings: PerfSettings;
  source?: ReservationSourceId[] | null;
  status?: ReservationStatusGroup[] | null;
  shifts?: ShiftFilter | null;
  connected?: ConnectedSourceId[];
};

/**
 * maître d'AI at panel size: the current thread next to any screen.
 *
 * `lane` is the column beside the page on wide screens. `drawer` is a modal
 * over the page (role="dialog"): focus moves in when it opens, Tab stays
 * inside, Escape closes it and focus returns to what opened it. Up to
 * SHEET_QUERY it fills the screen as a sheet, so a phone never gets a column.
 */
export function MaitrePanel({
  variant,
  onClose,
  contextLabel,
  suggestions,
  widgetData,
  unavailable,
  lastRestaurantView = false,
}: {
  variant: 'drawer' | 'lane';
  onClose: () => void;
  contextLabel?: string;
  suggestions?: string[];
  widgetData: MaitreWidgetData;
  /**
   * Why maître d'AI can't answer on this screen (Growth's demo group, whose
   * data is fictional). The panel then says so instead of offering a chat
   * that would answer about the person's own venues.
   */
  unavailable?: { title: string; body: string } | null;
  /**
   * The screen has no restaurant context of its own ("Your Google
   * accounts"): a new conversation uses the person's last restaurant view
   * and says so.
   */
  lastRestaurantView?: boolean;
}) {
  const { t } = useTranslation('assistant');
  const m = useMaitre();
  const board = useBoard(m.scopeKey);
  const [chatsOpen, setChatsOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const isLane = variant === 'lane';
  const isSheet = useNarrow(SHEET_QUERY) && !isLane;
  // Escape closes the chats menu first, then the panel.
  useDialogFocus(dialogRef, () => (chatsOpen ? setChatsOpen(false) : onClose()), !isLane);
  // What opened the panel, read while rendering: the composer takes focus in
  // its own effect, before the dialog hook can see the opener.
  const [opener] = useState(() =>
    typeof document !== 'undefined' && document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null,
  );
  useEffect(
    () => () => {
      if (!isLane && opener && document.contains(opener)) opener.focus();
    },
    [isLane, opener],
  );
  const dialogProps = isLane
    ? {}
    : {
        ref: dialogRef,
        role: 'dialog' as const,
        'aria-modal': true,
        'aria-labelledby': 'maitre-panel-title',
        tabIndex: -1,
      };
  const sheetClass = isSheet ? ' is-sheet' : '';
  const isNorthStar = m.isNorthStarFlow;
  const isEditing = isNorthStar && Boolean(m.existingNorthStar);
  const cards = cardMessages(m.messages);

  useEffect(() => {
    const el = bodyRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [m.messages, m.busy, m.activity]);

  useEffect(() => {
    if (!confirmDelete) return;
    const id = window.setTimeout(() => setConfirmDelete(false), 4000);
    return () => window.clearTimeout(id);
  }, [confirmDelete]);

  const title = isNorthStar
    ? isEditing
      ? t('ask.editNs')
      : t('ask.defineNs')
    : (m.activeThread?.title ??
      (isLane ? t('ask.rightHand') : t('ask.askName', { name: INSIGHTS_AGENT_NAME })));
  const description = isNorthStar
    ? isEditing
      ? t('ask.editNsDesc')
      : t('ask.defineNsDesc')
    : m.activeThread
      ? null
      : isLane
        ? t('ask.laneDesc')
        : t('ask.askDesc');
  const starters = isNorthStar
    ? NORTH_STAR_STARTER_KEYS.map(key => t(key))
    : suggestions?.length
      ? suggestions
      : (contextStarters(m.screenContext, 'panel') ?? ASK_STARTER_KEYS.map(key => t(key)));
  const showEmpty = m.messages.length === 0 && !m.busy;
  const widgetNeeds = widgetNeedsFromTypes(
    cards.flatMap(c => c.card.widgets ?? []).map(w => w.type),
  );

  if (unavailable) {
    return (
      <>
        {!isLane && <div className="scrim" onClick={onClose} role="presentation" />}
        <div
          className={`drawer agent-drawer maitre-panel is-unavailable${isLane ? ' agent-lane-panel' : ''}${sheetClass}`}
          data-ftue="agent-panel"
          {...dialogProps}
        >
          <div className="dr-head agent-head">
            <div className="agent-head-main">
              <span className="agent-head-badge" aria-hidden>
                <Icon name="bolt" size={16} />
              </span>
              <div className="agent-head-copy">
                <div className="agent-head-kicker">
                  {INSIGHTS_AGENT_NAME}
                  {contextLabel ? ` · ${contextLabel}` : ''}
                </div>
                <h2 className="agent-head-title" id={isLane ? undefined : 'maitre-panel-title'}>
                  {unavailable.title}
                </h2>
              </div>
            </div>
            <div className="agent-head-actions">
              <button type="button" className="ghost-btn maitre-close" onClick={onClose} aria-label={t('ask.close')}>
                <Icon name="close" size={18} />
              </button>
            </div>
          </div>
          <div className="dr-body agent-body">
            <p className="agent-empty-lead" role="status">
              {unavailable.body}
            </p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      {!isLane && <div className="scrim" onClick={onClose} role="presentation" />}
      <div
        className={`drawer agent-drawer maitre-panel${isNorthStar ? ' north-star-drawer' : ''}${isLane ? ' agent-lane-panel' : ''}${sheetClass}`}
        data-ftue="agent-panel"
        {...dialogProps}
      >
        <div className="dr-head agent-head">
          <div className="agent-head-main">
            <span className={`agent-head-badge${isNorthStar ? ' ns-head-badge' : ''}`} aria-hidden>
              <Icon name={isNorthStar ? 'star' : 'bolt'} size={16} />
            </span>
            <div className="agent-head-copy">
              <div className="agent-head-kicker">
                {INSIGHTS_AGENT_NAME}
                {contextLabel ? ` · ${contextLabel}` : ''}
              </div>
              <h2 className="agent-head-title" id={isLane ? undefined : 'maitre-panel-title'}>
                {title}
              </h2>
              {description ? <p className="agent-head-desc">{description}</p> : null}
            </div>
          </div>
          <div className="agent-head-actions">
            <button
              type="button"
              className={`maitre-head-btn${chatsOpen ? ' is-on' : ''}`}
              onClick={() => setChatsOpen(open => !open)}
              aria-expanded={chatsOpen}
              title={t('maitre.chats')}
              aria-label={t('maitre.chats')}
            >
              <Icon name="chats" size={15} />
            </button>
            <button
              type="button"
              className="maitre-head-btn"
              onClick={() => {
                setChatsOpen(false);
                m.newThread();
              }}
              title={t('ask.newChat')}
              aria-label={t('ask.newChat')}
            >
              <Icon name="plus" size={15} />
            </button>
            <button
              type="button"
              className="maitre-head-btn"
              onClick={m.expand}
              title={t('maitre.expand')}
              aria-label={t('maitre.expand')}
            >
              <Icon name="expand" size={15} />
            </button>
            {isEditing ? (
              <button
                type="button"
                className={`agent-newchat ns-delete-btn${confirmDelete ? ' confirm' : ''}`}
                onClick={() => {
                  if (!confirmDelete) {
                    setConfirmDelete(true);
                    return;
                  }
                  setConfirmDelete(false);
                  void m.deleteNorthStar();
                }}
                disabled={m.busy || m.saving || m.deleting}
              >
                <Icon name="ban" size={13} />
                {m.deleting ? t('ask.deleting') : confirmDelete ? t('ask.confirmDelete') : t('ask.delete')}
              </button>
            ) : null}
            <button type="button" className="ghost-btn maitre-close" onClick={onClose} aria-label={t('ask.close')}>
              <Icon name="close" size={18} />
            </button>
          </div>
          {chatsOpen ? (
            <ChatList
              variant="menu"
              threads={m.threads}
              activeId={m.activeThread?.id ?? null}
              onOpen={id => {
                setChatsOpen(false);
                void m.openThread(id);
              }}
              onNew={() => {
                setChatsOpen(false);
                m.newThread();
              }}
              onDelete={id => void m.deleteThread(id)}
            />
          ) : null}
        </div>

        {/* The conversation's own context (Phase C): a new chat shows the
            screen's, which its first question records; it never follows the
            screen afterwards, and asks once when the screen shows another. */}
        <ConversationContextRow
          state={m.contextState}
          context={m.conversationContext}
          screenContext={m.screenContext}
          lastRestaurantView={lastRestaurantView}
        />
        {m.askContextChange && m.conversationContext && m.screenContext ? (
          <ContextChangePrompt
            context={m.conversationContext}
            screenContext={m.screenContext}
            onUse={() => void m.takeScreenContext()}
            onKeep={m.keepContext}
          />
        ) : null}

        <AssistantWidgetDataProvider
          restaurantId={widgetData.restaurantId}
          regionFilter={widgetData.regionFilter}
          perfSettings={widgetData.perfSettings}
          source={widgetData.source}
          status={widgetData.status}
          shifts={widgetData.shifts}
          connected={widgetData.connected}
          needs={widgetNeeds}
        >
          <div className="dr-body agent-body" ref={bodyRef}>
            {showEmpty ? (
              <div className="agent-empty">
                <p className="agent-empty-lead">{t('ask.tryThese')}</p>
                <div className="agent-suggestions">
                  {starters.map(s => (
                    <button key={s} type="button" className="agent-chip" onClick={() => void m.ask(s)}>
                      {s}
                    </button>
                  ))}
                  {!isNorthStar ? (
                    <button type="button" className="agent-chip is-north-star" onClick={m.startNorthStar}>
                      <Icon name="star" size={12} />
                      {m.existingNorthStar ? t('ask.editNs') : t('ask.defineNs')}
                    </button>
                  ) : null}
                </div>
              </div>
            ) : null}
            <ThreadMessages
              messages={m.messages}
              variant="panel"
              busy={m.busy}
              activity={m.activity}
              onAction={action => void m.ask(action.question)}
              onRetry={m.retry}
              pinsFor={board.pinsFor}
              onSwitchContext={id => void m.switchContext(id)}
            />
            {m.awaitingProceed ? (
              <div className="agent-proceed">
                <button type="button" className="agent-proceed-btn" onClick={m.proceed}>
                  {t('ask.proceed')}
                </button>
              </div>
            ) : null}
          </div>
        </AssistantWidgetDataProvider>

        {board.toast ? (
          <p className="maitre-panel-toast" role="status">
            {board.toast}
          </p>
        ) : null}

        {isNorthStar && m.northStarDraft ? (
          <div className="ns-save-bar">
            <div className="ns-save-copy">
              <strong>{m.northStarDraft.label}</strong>
              <span>
                {t('ask.baseline')}{' '}
                {formatNorthStarValueInline(m.northStarDraft.baseline.value, m.northStarDraft.spec.unit)}{' '}
                · {m.northStarDraft.baseline.window.start} – {m.northStarDraft.baseline.window.end}
              </span>
            </div>
            <button
              type="button"
              className="tb-btn accent"
              onClick={() => void m.saveNorthStar()}
              disabled={m.saving || m.busy}
            >
              {m.saving ? t('ask.saving') : t('ask.saveNs')}
            </button>
          </div>
        ) : null}

        <MaitreComposer
          variant="panel"
          busy={m.busy}
          onSend={text => void m.ask(text)}
          onStop={m.cancel}
          focus={m.focus}
          onClearFocus={() => m.setFocus(null)}
          shifts={m.shifts}
          placeholder={isNorthStar ? t('ask.phNs') : t('ask.phAsk')}
          autoFocus={!isLane}
        />
      </div>
    </>
  );
}
